import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  type LayoutSkeleton,
  layoutSkeletonSchema,
} from '../composables/data-fetching/auto-layout/index.types'
import type { UserFilteringData } from '../plugins/1.filters'

import { getConfigStorePath } from '../utils/getConfigStorePath'

/**
 * If device is mobile, modifies the layout data to remove all ads from the first section and only returns one per remaining sections.
 * @param layoutData - The layout data to be modified
 * @returns {Promise<LayoutSkeleton>} - The modified layout data
 */
const getDeviceLayout = (layoutData: LayoutSkeleton) => {
  const layoutCopy = structuredClone(layoutData)
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const { $filters } = useNuxtApp() as unknown as {
    $filters: UserFilteringData
  }
  if (!$filters) {
    throw new Error(
      'Invalid or missing $filters in Nuxt app. Expected $filters to be an object with an "isMobile" property.'
    )
  }
  // no layout change needed
  if (!$filters?.isMobile || !layoutCopy.section) return layoutCopy

  // Note: Currently, there are no defined rules for handling ads in mobile layouts.
  // This is a temporary solution, explicit rules should be defined to handle this logic more robustly.
  layoutCopy?.section.map((section, index) => {
    if (index === 0 || !section.ads?.length) {
      section.ads = []
      return
    }

    // target last index of x to avoid 2 ad units in a row
    const selectedAd = section.ads?.filter((ad) => ad.x === -1).slice(-1)
    section.ads = selectedAd ? selectedAd : section.ads.slice(-1)
  })
  return layoutCopy
}

/**
 * Loads layout skeleton JSON from remote CDN based on scope (tenant or default)
 * Validates the JSON schema using Zod
 * @param scope - Determines which skeleton layout file to load ('tenant' or 'default')
 * @returns {Promise<LayoutSkeleton | undefined>} - Parsed and validated LayoutSkeleton object, or undefined if invalid/missing
 */
const loadLayoutSkeletonFile = async (
  scope: 'tenant' | 'default'
): Promise<LayoutSkeleton | undefined> => {
  try {
    const response = await fetch(await getConfigStorePath('layoutSkeleton'))
    if (!response.ok) {
      console.error(
        `Failed to load ${scope} layout skeleton: ${response.status} ${response.statusText}`
      )
      return undefined
    }
    const data = await response.json()

    const deviceLayout = getDeviceLayout(data)

    const parsedData = layoutSkeletonSchema.safeParse(deviceLayout)
    if (parsedData.success) {
      return parsedData.data
    } else {
      console.error(
        `Invalid ${scope}layout skeleton format:`,
        parsedData.error.format()
      )
      return undefined
    }
  } catch (error) {
    console.error('Error loading layout skeleton', error)
    return undefined
  }
}

const replaceSkeletonPrefixes = (skeleton: LayoutSkeleton): LayoutSkeleton => {
  const prefix = useRuntimeConfig().public.sitePrefix
  if (!prefix) {
    console.warn('No site prefix found, using default')
    return skeleton
  }

  const replacePrefix = (str: string) => {
    return str.replace(/^default_/, `${prefix}_`)
  }

  return {
    ...skeleton,
    section: skeleton.section.map((section) => ({
      ...section,
      ads: section.ads?.map((ad) => ({
        ...ad,
        classes: ad.classes ? replacePrefix(ad.classes) : ad.classes,
      })),
    })),
  }
}

const getLayoutSkeleton = async (): Promise<LayoutSkeleton | undefined> => {
  const tenantLayoutSkeleton = await loadLayoutSkeletonFile('tenant')
  if (tenantLayoutSkeleton) return tenantLayoutSkeleton

  const defaultLayoutSkeleton = await loadLayoutSkeletonFile('default')
  if (defaultLayoutSkeleton) {
    return replaceSkeletonPrefixes(defaultLayoutSkeleton)
  }

  console.warn('No valid layout skeleton found')
  return undefined
}

/**
 * Pinia store for managing the layout skeleton
 * This store fetches the layout skeleton from a remote CDN and provides methods to refresh it
 * It also handles loading state and provides a reactive reference to the layout skeleton
 */
export const useLayoutSkeleton = defineStore('layoutSkeleton', () => {
  const layoutSkeleton = ref<LayoutSkeleton | undefined>(undefined)
  const isLoading = ref(true)

  const refreshLayoutSkeleton = async () => {
    isLoading.value = true
    const data = await getLayoutSkeleton()
    if (data) {
      layoutSkeleton.value = data
    } else {
      console.error('No valid layout skeleton found, using undefined')
    }
    isLoading.value = false
  }

  refreshLayoutSkeleton()

  return {
    layoutSkeleton,
    isLoading,
    refreshLayoutSkeleton,
  }
})
